import React from 'react'
import styled from 'styled-components'
import {CircleAlert} from 'lucide-react'
import {Skeleton} from '../../BaseComponents'
import {InfoHint} from '../InfoHint'

/**
 * StatValue (internal)
 *
 * What one figure's value slot shows, for every DS figure component (KeyValueList, StatGrid,
 * ComparisonBars, StackedBar and the BarList rows), so a state reads the same wherever it appears.
 * The slot always renders something; precedence is loading > error > pending > empty > value:
 * - loading: a skeleton one line high, hidden from assistive tech (the list or grid around it
 *   carries aria-busy);
 * - error: 'Couldn’t load' with an icon named "Error", in error ink. Never '—', so a failure can't
 *   read as "no data";
 * - pending: text standing in for the figure ('Not tracked yet', 'Reconnect Zoom'), in secondary
 *   ink. With `pendingReason` that text is an InfoHint trigger that explains it;
 * - empty: '—'. With `emptyReason` the '—' is an InfoHint trigger that explains it;
 * - value: the value as given.
 *
 * A reason shows only in its own state, so a slot that is loading or failed never shows one meant
 * for its '—'. A slot that already has an info trigger (a cell with a definition) passes no reason
 * and appends it to that trigger instead: one trigger per figure.
 *
 * The state text never grows past body size and is always in the sans face, so a failure in a
 * large or mono value slot doesn't shout or read as a number.
 *
 * @example
 *   <StatValue value={formatted} loading={loading} error={error} pending={notTracked} />
 *   <StatValue value={null} emptyReason="No orders this period" reasonLabel="About Assist ratio" />
 *   <StatValue pending="Reconnect Zoom" pendingReason={whyReconnect} reasonTestId={`${id}-info`} />
 */

export type StatValueState = 'loading' | 'error' | 'pending' | 'empty' | 'value'

export interface StatValueStateInput {
  /** The figure. null, undefined, '' and false are empty; 0 is a value. */
  value?: React.ReactNode
  loading?: boolean
  /** Truthy = the figure could not be read. The same type as DashboardCard's `error`. */
  error?: Error | null | boolean
  /** Text in place of the figure, e.g. 'Not tracked yet'. Blank means none. */
  pending?: React.ReactNode
}

export interface StatValueProps extends StatValueStateInput {
  /** The error text (default "Couldn’t load"). */
  errorLabel?: string
  /** Why the figure is '—'. Makes the '—' an InfoHint trigger with this as its content. */
  emptyReason?: React.ReactNode
  /** Why the figure reads its pending text. Makes that text an InfoHint trigger with this content. */
  pendingReason?: React.ReactNode
  /**
   * The trigger's accessible name, e.g. "About Assist ratio"; the '—' trigger's default is "No
   * figure". A pending trigger's name also carries its visible words ("About Job orders: Reconnect
   * Zoom"), so it doesn't announce as a button that does what the words say.
   */
  reasonLabel?: string
  /** On the '—' or pending trigger. */
  reasonTestId?: string
  /** The skeleton's width (default 4em: a short figure at the slot's own size). */
  skeletonWidth?: string
}

export const EMPTY_VALUE = '—'
export const DEFAULT_ERROR_LABEL = 'Couldn’t load'

/** True for what renders nothing: null, undefined, '' and false. */
export const isBlankNode = (node: React.ReactNode): boolean =>
  node === null || node === undefined || node === '' || node === false

/** Which state a figure's slot is in, by the shared precedence. */
export function statValueState({
  value,
  loading,
  error,
  pending,
}: StatValueStateInput): StatValueState {
  if (loading) return 'loading'
  if (error) return 'error'
  if (!isBlankNode(pending)) return 'pending'
  if (isBlankNode(value)) return 'empty'
  return 'value'
}

/** 1em high, so the skeleton takes the value's own line box; inline beside a label. */
const ValueSkeleton = styled(Skeleton)`
  display: inline-block;
  vertical-align: middle;
`

/** The non-value states: the sans face, and no bigger than body text inside a large value. */
const StateText = styled.span`
  font-family: var(--font-sans);
  font-size: min(1em, var(--fs-body));
  font-weight: var(--fw-medium);
  letter-spacing: normal;
`

const ErrorText = styled(StateText)`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--error-800);

  svg {
    flex-shrink: 0;
  }
`

const PendingText = styled(StateText)`
  color: var(--text-secondary);
`

/** The '—' keeps the slot's size, like the value it stands in for. */
const EmptyText = styled.span`
  font-family: var(--font-sans);
  color: var(--text-secondary);
`

const DEFAULT_REASON_LABEL = 'No figure'

/** "About Job orders: Reconnect Zoom": the row it is about, then the words it shows. */
function pendingTriggerLabel(pending: React.ReactNode, reasonLabel: string | undefined): string {
  const words = typeof pending === 'string' || typeof pending === 'number' ? String(pending) : ''
  if (!words) return reasonLabel ?? DEFAULT_REASON_LABEL
  return reasonLabel ? `${reasonLabel}: ${words}` : words
}

export function StatValue({
  value,
  loading = false,
  error,
  pending,
  errorLabel = DEFAULT_ERROR_LABEL,
  emptyReason,
  pendingReason,
  reasonLabel,
  reasonTestId,
  skeletonWidth = '4em',
}: StatValueProps) {
  switch (statValueState({value, loading, error, pending})) {
    case 'loading':
      return <ValueSkeleton aria-hidden="true" width={skeletonWidth} height="1em" />
    case 'error':
      return (
        <ErrorText>
          <CircleAlert size="1em" role="img" aria-label="Error" />
          {errorLabel}
        </ErrorText>
      )
    case 'pending':
      return (
        <PendingText>
          {isBlankNode(pendingReason) ? (
            pending
          ) : (
            <InfoHint
              label={pendingTriggerLabel(pending, reasonLabel)}
              content={pendingReason}
              data-testid={reasonTestId}>
              {pending}
            </InfoHint>
          )}
        </PendingText>
      )
    case 'empty':
      return (
        <EmptyText>
          {isBlankNode(emptyReason) ? (
            EMPTY_VALUE
          ) : (
            <InfoHint
              label={reasonLabel ?? DEFAULT_REASON_LABEL}
              content={emptyReason}
              data-testid={reasonTestId}>
              {EMPTY_VALUE}
            </InfoHint>
          )}
        </EmptyText>
      )
    default:
      return <>{value}</>
  }
}
